import React, { useState, useEffect } from "react";
import { Card, Button, Alert, Badge } from "react-bootstrap";
import { CleanupScheduler } from "../services/cleanupScheduler";
import { toast } from "react-toastify";

export default function CleanupManagement() {
  const [loading, setLoading] = useState(false);
  const [lastCleanup, setLastCleanup] = useState(null);
  const [nextCleanup, setNextCleanup] = useState(null);
  const [daysUntilNext, setDaysUntilNext] = useState(0);

  useEffect(() => {
    updateCleanupInfo();
  }, []);

  const updateCleanupInfo = () => {
    setLastCleanup(CleanupScheduler.getLastCleanupDate());
    setNextCleanup(CleanupScheduler.getNextCleanupDate());
    setDaysUntilNext(CleanupScheduler.getDaysUntilNextCleanup());
  };

  const handleForceCleanup = async () => {
    setLoading(true);
    try {
      const result = await CleanupScheduler.forceCleanup();
      updateCleanupInfo();
      toast.success(
        `Cleanup completed! Removed ${result.deletedCount} old activities.`
      );
    } catch (error) {
      toast.error("Failed to run cleanup");
      console.error(error);
    }
    setLoading(false);
  };

  const getStatusColor = () => {
    if (daysUntilNext <= 1) return "success";
    if (daysUntilNext <= 3) return "warning";
    return "info";
  };

  const getStatusText = () => {
    if (daysUntilNext === 0) return "Cleanup due today";
    if (daysUntilNext === 1) return "Cleanup due tomorrow";
    return `Cleanup in ${daysUntilNext} days`;
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">🧹 Activity Cleanup Management</h5>
          <Badge bg={getStatusColor()}>{getStatusText()}</Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="row mb-3">
          <div className="col-md-6">
            <strong>Last Cleanup:</strong>
            <div className="text-muted">
              {lastCleanup
                ? lastCleanup.toLocaleDateString() +
                  " at " +
                  lastCleanup.toLocaleTimeString()
                : "Never"}
            </div>
          </div>
          <div className="col-md-6">
            <strong>Next Scheduled:</strong>
            <div className="text-muted">
              {nextCleanup ? nextCleanup.toLocaleDateString() : "Not scheduled"}
            </div>
          </div>
        </div>

        <Alert variant="info" className="mb-3">
          <strong>Automatic Cleanup:</strong> The system automatically removes
          student activities older than one week every 7 days to maintain
          optimal performance.
        </Alert>

        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleForceCleanup}
            disabled={loading}
          >
            {loading ? "Running Cleanup..." : "🗑️ Run Cleanup Now"}
          </Button>

          <Button variant="outline-secondary" onClick={updateCleanupInfo}>
            📊 Refresh Status
          </Button>
        </div>

        <div className="mt-3">
          <small className="text-muted">
            <strong>How it works:</strong> Activities older than 7 days are
            automatically removed to keep the database clean and improve
            performance. Recent activities (within the last week) are always
            preserved.
          </small>
        </div>
      </Card.Body>
    </Card>
  );
}
